<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '📦 Apa itu Harga Grosir Bertingkat?',
    icon: 'ri-price-tag-3-line',
    color: 'primary',
    content: `
      <p class="mb-4">
        Fitur ini dipakai kalau Anda ingin memberi <strong>harga lebih murah per
        satuan</strong> ketika pelanggan membeli dalam jumlah banyak — misalnya beli 1 pcs
        harga normal Rp5.000, tapi beli 1 renceng (12 pcs) harga per pcs-nya turun jadi
        Rp4.500.
      </p>
      <VAlert type="info" variant="tonal">
        💡 Ini bukan diskon terpisah yang harus dipilih kasir secara manual — begitu tier
        sudah diatur, harga otomatis menyesuaikan sendiri di keranjang berdasarkan jumlah
        yang dibeli.
      </VAlert>
    `,
  },
  {
    title: '⚙️ Cara Mengatur Tier',
    icon: 'ri-settings-3-line',
    color: 'success',
    content: `
      <p class="mb-4">
        Tier grosir diatur <strong>per varian produk</strong>, dari halaman
        <strong>Edit Produk</strong> — produk harus sudah tersimpan dulu (varian baru yang
        belum disimpan belum bisa diatur tier-nya).
      </p>
      <ol class="mb-4">
        <li class="mb-2">Buka menu <strong>Produk</strong>, klik produk yang ingin diatur,
          lalu klik <strong>Edit</strong></li>
        <li class="mb-2">Pada varian yang dituju, klik tombol <strong>"Tier Grosir"</strong></li>
        <li class="mb-2">Klik <strong>"Tambah Tier"</strong>, lalu isi:</li>
      </ol>
      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Field</th><th>Penjelasan</th><th>Contoh</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Minimal Qty</strong></td>
            <td>Jumlah beli minimal supaya harga tier ini berlaku</td>
            <td>12</td>
          </tr>
          <tr>
            <td><strong>Harga per Unit</strong></td>
            <td>Harga baru untuk 1 satuan produk, kalau syarat qty minimal terpenuhi</td>
            <td>Rp4.500</td>
          </tr>
        </tbody>
      </table>
      <ol start="4" class="mb-4">
        <li class="mb-2">Boleh tambah lebih dari 1 tier (mis. beli 12 → Rp4.500/pcs, beli
          24 → Rp4.200/pcs) — klik <strong>"Tambah Tier"</strong> lagi untuk baris baru</li>
        <li class="mb-0">Klik <strong>"Simpan Tier Grosir"</strong></li>
      </ol>
      <VAlert type="warning" variant="tonal">
        ⚠️ Kosongkan/hapus semua tier (klik ikon tempat sampah di tiap baris, lalu simpan)
        untuk mengembalikan produk ke harga varian biasa tanpa tier grosir.
      </VAlert>
    `,
  },
  {
    title: '🧮 Cara Kerja Saat Checkout',
    icon: 'ri-calculator-line',
    color: 'warning',
    content: `
      <p class="mb-4">
        Saat kasir menambahkan produk ke keranjang, sistem otomatis memeriksa jumlah
        (quantity) produk itu di keranjang, lalu memilih <strong>tier dengan Minimal Qty
        tertinggi yang masih terpenuhi</strong> oleh jumlah tersebut.
      </p>
      <p class="mb-4">
        Contoh dengan tier di atas (beli 12 → Rp4.500/pcs, beli 24 → Rp4.200/pcs, harga
        normal Rp5.000/pcs):
      </p>
      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Jumlah di Keranjang</th><th>Harga per Pcs yang Dipakai</th></tr>
        </thead>
        <tbody>
          <tr><td>1–11 pcs</td><td>Rp5.000 (harga normal, belum kena tier)</td></tr>
          <tr><td>12–23 pcs</td><td>Rp4.500 (tier pertama)</td></tr>
          <tr><td>24 pcs atau lebih</td><td>Rp4.200 (tier tertinggi)</td></tr>
        </tbody>
      </table>
      <VAlert type="success" variant="tonal">
        ✅ Tidak ada langkah tambahan buat kasir — harga di keranjang dan struk sudah
        otomatis memakai harga tier yang sesuai, sama seperti perhitungan diskon
        promo/voucher yang sudah berjalan selama ini.
      </VAlert>
    `,
  },
]
</script>

<template>
  <div>
    <RouterLink to="/userguide/retail">
      <VBtn
        variant="text"
        prepend-icon="ri-arrow-left-line"
        class="mb-2"
      >
        Kembali ke Fitur Retail
      </VBtn>
    </RouterLink>

    <div class="d-flex align-center mb-4">
      <VAvatar
        color="warning"
        variant="tonal"
        size="64"
        rounded="lg"
        class="me-4"
      >
        <VIcon
          icon="ri-price-tag-3-line"
          size="36"
        />
      </VAvatar>
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">
          Harga Grosir Bertingkat
        </h4>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Harga lebih murah per satuan untuk pembelian dalam jumlah banyak
        </p>
      </div>
    </div>

    <VExpansionPanels
      variant="accordion"
      multiple
    >
      <VExpansionPanel
        v-for="(section, index) in sections"
        :key="index"
      >
        <VExpansionPanelTitle>
          <VIcon
            :icon="section.icon"
            :color="section.color"
            class="me-3"
          />
          {{ section.title }}
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div v-html="section.content" />
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- FAQ -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      ❓ Pertanyaan yang Sering Ditanyakan
    </h5>
    <VExpansionPanels variant="accordion">
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Kenapa tombol "Tier Grosir" tidak muncul saat menambah produk baru?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tier grosir baru bisa diatur setelah varian punya ID (sudah tersimpan ke
          database) — sama seperti tombol Recipe yang juga baru aktif setelah varian
          disimpan. Simpan produk terlebih dulu, lalu buka halaman Edit untuk mengatur
          tier-nya.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Apakah tier grosir berlaku juga untuk pesanan self-order/online?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Perhitungan harga berdasarkan jumlah beli ini adalah bagian dari mesin hitung
          harga yang sama dipakai di seluruh alur transaksi, jadi berlaku konsisten di
          mana pun produk itu dijual lewat aplikasi.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Bisakah tier grosir digabung dengan promo/voucher/poin?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Bisa. Harga tier grosir dihitung lebih dulu sebagai harga dasar per item,
          baru diskon promo/voucher/poin dihitung di atasnya — urutan ini sudah
          terintegrasi otomatis, tidak perlu diatur manual oleh kasir.
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- Related -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      🔗 Halaman Terkait
    </h5>
    <VRow>
      <VCol
        v-for="link in [
          { title: 'Toggle F&B & Retail', sub: 'Aktifkan dulu fitur Retail di Pengaturan', icon: 'ri-store-3-line', color: 'primary', to: { name: 'userguide-retail-toggle' } },
          { title: 'Produk', sub: 'Kelola katalog produk dan variant', icon: 'ri-shopping-bag-3-line', color: 'info', to: { name: 'userguide-produk-inventory-produk' } },
          { title: 'Gabungan di 1 Transaksi', sub: 'Pakai promo + voucher + poin bersamaan', icon: 'ri-percent-line', color: 'pink', to: { name: 'userguide-promo-voucher-loyalty-kombinasi' } },
        ]"
        :key="link.title"
        cols="12"
        md="6"
      >
        <RouterLink
          :to="link.to"
          class="text-decoration-none"
        >
          <VCard
            variant="outlined"
            hover
          >
            <VCardText class="d-flex align-center gap-3 py-3">
              <VAvatar
                :color="link.color"
                variant="tonal"
                size="40"
                rounded
              >
                <VIcon
                  :icon="link.icon"
                  size="22"
                />
              </VAvatar>
              <div>
                <p class="text-body-2 font-weight-bold mb-0">
                  {{ link.title }}
                </p>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ link.sub }}
                </p>
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.userguide-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.userguide-table th,
.userguide-table td {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.userguide-table th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-weight: 600;
}
</style>
